// frontend/src/features/equipment/EquipmentSetupWizard/components/BrandModelInput.tsx
import { Select, TextInput, Stack, Grid } from '@mantine/core';
import { useBrandsForSubcategory, useModelsForBrand } from '@/hooks/useEquipmentCatalog';
import type { BrandModelValue } from '../types';

const OTHER_OPTION_VALUE = '__other__';
const MAX_INPUT_LENGTH = 100;

// Sanitize user input by limiting length (trimming is done on save, not during typing)
function sanitizeInput(value: string): string {
  return value.slice(0, MAX_INPUT_LENGTH);
}

interface BrandModelInputProps {
  subcategoryId: string;
  value: BrandModelValue;
  onChange: (value: BrandModelValue) => void;
  disabled?: boolean;
}

export function BrandModelInput({
  subcategoryId,
  value,
  onChange,
  disabled = false,
}: BrandModelInputProps) {
  const brands = useBrandsForSubcategory(subcategoryId);
  const models = useModelsForBrand(
    value.brandId !== OTHER_OPTION_VALUE ? value.brandId : undefined,
    subcategoryId
  );

  const isOtherBrand = value.brandId === OTHER_OPTION_VALUE;
  const isOtherModel = value.modelId === OTHER_OPTION_VALUE;

  // Build brand options with "Other" at the end
  const brandOptions = [
    ...brands.map((brand) => ({ value: brand.id, label: brand.name })),
    { value: OTHER_OPTION_VALUE, label: 'Other' },
  ];

  // Build model options with "Other" at the end
  const modelOptions = [
    ...models.map((model) => ({ value: model.id, label: model.name })),
    { value: OTHER_OPTION_VALUE, label: 'Other' },
  ];

  const handleBrandChange = (brandId: string | null) => {
    if (brandId) {
      onChange({
        brandId,
        brandName: undefined,
        modelId: undefined,
        modelName: undefined,
      });
    }
  };

  const handleModelChange = (modelId: string | null) => {
    if (modelId) {
      onChange({
        ...value,
        modelId,
        modelName: undefined,
      });
    }
  };

  const handleBrandNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const sanitized = sanitizeInput(event.target.value);
    onChange({
      ...value,
      brandName: sanitized,
    });
  };

  const handleModelNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const sanitized = sanitizeInput(event.target.value);
    onChange({
      ...value,
      modelName: sanitized,
    });
  };

  return (
    <Stack gap="sm">
      <Grid>
        <Grid.Col span={6}>
          <Select
            label="Brand"
            placeholder="Select brand"
            data={brandOptions}
            value={value.brandId || null}
            onChange={handleBrandChange}
            disabled={disabled}
            searchable
            clearable={false}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          {isOtherBrand ? (
            <TextInput
              label="Brand Name"
              placeholder="Enter brand name"
              value={value.brandName || ''}
              onChange={handleBrandNameChange}
              disabled={disabled}
              maxLength={MAX_INPUT_LENGTH}
            />
          ) : (
            <Select
              label="Model"
              placeholder="Select model"
              data={modelOptions}
              value={value.modelId || null}
              onChange={handleModelChange}
              disabled={disabled || !value.brandId}
              searchable
              clearable={false}
            />
          )}
        </Grid.Col>
      </Grid>

      {/* Show model name input for "Other" brand or "Other" model */}
      {(isOtherBrand || isOtherModel) && (
        <Grid>
          {isOtherBrand && (
            <Grid.Col span={6} offset={6}>
              <TextInput
                label="Model Name"
                placeholder="Enter model name"
                value={value.modelName || ''}
                onChange={handleModelNameChange}
                disabled={disabled}
                maxLength={MAX_INPUT_LENGTH}
              />
            </Grid.Col>
          )}
          {!isOtherBrand && isOtherModel && (
            <Grid.Col span={6} offset={6}>
              <TextInput
                label="Model Name"
                placeholder="Enter model name"
                value={value.modelName || ''}
                onChange={handleModelNameChange}
                disabled={disabled}
                maxLength={MAX_INPUT_LENGTH}
              />
            </Grid.Col>
          )}
        </Grid>
      )}
    </Stack>
  );
}
